"use client";

import { useTina, tinaField, coloredText, getIcon, cn } from "@/lib/utils";
import Pill from "@/components/Pill";
import Section from "@/components/Section";
import CallToAction from "@/components/CallToAction";
import Link from "@/components/ui/link";

import Image from "next/image";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

import { CircleCheck } from "lucide-react";
import TextTooltip from "@/components/TextTooltip";

export default function HomePage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const {
    header,
    whatWeDo,
    valueProposition,
    whoWeServe,
    socialProof,
    callToAction,
  } = data.home;
  return (
    <div>
      <Header header={header} />
      <WhatWeDo whatWeDo={whatWeDo} />
      <ValueProposition valueProposition={valueProposition} />
      <WhoWeServe whoWeServe={whoWeServe} />
      <SocialProof socialProof={socialProof} />
      <CallToAction callToAction={callToAction} />
    </div>
  );
}

const Header = ({ header }) => {
  const title = coloredText(header?.title);
  return (
    <Section
      as="header"
      wrapperClassName="!pb-8 overflow-hidden bg-gradient-to-b from-background-blackish from-70% to-70% to-background-purplish"
      className="grid grid-cols-[1fr] gap-20 items-center"
    >
      <div className="flex flex-col gap-8 items-center text-center">
        <h1
          className="h0 text-balance max-w-[1000px]"
          data-tina-field={tinaField(header, "title")}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <TextTooltip
          text={header?.subtitle}
          className="p-xl text-foreground-300 text-balance max-w-[600px]"
          data-tina-field={tinaField(header, "subtitle")}
        />
        {(header?.button?.label || header?.button?.link) && (
          <Button
            size="lg"
            data-tina-field={tinaField(header, "button")}
            asChild
          >
            <Link href={header?.button?.link}>{header?.button?.label}</Link>
          </Button>
        )}
      </div>
      <div className="bg-background-purplish rounded-lg overflow-hidden p-2 w-full max-w-[1000px] mx-auto ">
        <Carousel
          plugins={[
            Autoplay({
              delay: header?.carouselSpeed * 1000 || 2000,
              stopOnMouseEnter: true,
              stopOnInteraction: false,
            }),
          ]}
        >
          <CarouselContent className="cursor-grab focus:cursor-grabbing active:cursor-grabbing">
            {header?.images?.map((image, index) => (
              <CarouselItem key={index}>
                <Image
                  src={image?.image || "https://ui.shadcn.com/placeholder.svg"}
                  alt={image?.title ?? "Aberrant Screenshot"}
                  width={500}
                  height={1000}
                  className="w-full h-full aspect-[16/9] object-cover object-top-left rounded-lg"
                  quality={100}
                  data-tina-field={tinaField(header, `images.${index}.image`)}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselDots />
        </Carousel>
        {/* <Image
          src={header?.image}
          alt="Aberrant Screenshot"
          width={500}
          height={1000}
          className="w-full h-full aspect-[16/9] object-cover object-top-left rounded-lg"
          quality={100}
          data-tina-field={tinaField(header, "image")}
        /> */}
      </div>
      {/* <div className="bg-background-purplish rounded-lg overflow-hidden p-2 w-full max-w-[1000px] mx-auto">
        <Image
          src={header?.image}
          alt="Aberrant Screenshot"
          width={500}
          height={1000}
          className="w-full h-full aspect-[16/9] object-cover object-top-left rounded-lg"
          quality={100}
          data-tina-field={tinaField(header, "image")}
        />
      </div> */}
    </Section>
  );
};

const WhatWeDo = ({ whatWeDo }) => {
  return (
    <Section
      wrapperClassName="bg-background-purplish overflow-hidden"
      className="flex flex-col gap-12"
    >
      <div className="flex flex-col gap-8 items-center">
        <Pill data-tina-field={tinaField(whatWeDo, "sectionTitle")}>
          {whatWeDo?.sectionTitle}
        </Pill>
        <h2
          className="h1 max-w-[800px] text-center"
          data-tina-field={tinaField(whatWeDo, "title")}
        >
          {whatWeDo?.title}
        </h2>
        <TextTooltip
          text={whatWeDo?.description}
          className="p-xl text-foreground-300 max-w-[850px] text-center"
          data-tina-field={tinaField(whatWeDo, "description")}
        />
      </div>

      <Carousel className="[&>div]:!overflow-visible cursor-grab focus:cursor-grabbing active:cursor-grabbing">
        <CarouselContent className="-ml-8">
          {whatWeDo?.items?.map((item, index) => {
            const Icon = getIcon(item?.icon);
            return (
              <CarouselItem
                key={index}
                className="basis-full lg:basis-1/2 !pl-8"
              >
                <div className="card h-full bg-background-blackish overflow-hidden">
                  {Icon && (
                    <div
                      data-tina-field={tinaField(
                        whatWeDo,
                        `items.${index}.icon`
                      )}
                    >
                      <Icon className="text-primary-300 size-8 stroke-[1.5px]" />
                    </div>
                  )}
                  <h3
                    className="h4 !font-bold mt-6 mb-4"
                    data-tina-field={tinaField(
                      whatWeDo,
                      `items.${index}.title`
                    )}
                  >
                    {item?.title}
                  </h3>
                  <TextTooltip
                    text={item?.description}
                    className="p text-foreground-500"
                    data-tina-field={tinaField(
                      whatWeDo,
                      `items.${index}.description`
                    )}
                  />
                  <div
                    // className="bg-background-purplish rounded-lg overflow-hidden translate-y-16 translate-x-12"
                    className="bg-background-purplish rounded-lg overflow-hidden translate-y-16 p-4"
                    data-tina-field={tinaField(
                      whatWeDo,
                      `items.${index}.image`
                    )}
                  >
                    <Image
                      src={item?.image}
                      alt={`${item?.title} Screenshot`}
                      width={558}
                      height={314}
                      quality={100}
                      // className="w-full h-full object-cover translate-y-8 translate-x-8 aspect-[16/9] object-top-left"
                      className="w-full h-full object-cover aspect-[16/9] object-top-left"
                    />
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        <div className="flex gap-6 justify-center mt-12">
          <CarouselPrevious className="relative translate-0 left-0 w-12 h-12 bg-transparent border-primary-500 border-2 rounded-full [&>svg]:!size-5 hover:bg-primary-500/30 hover:text-white" />
          <CarouselNext className="relative translate-0 right-0 w-12 h-12 bg-transparent border-primary-500 border-2 rounded-full [&>svg]:!size-5 hover:bg-primary-500/30 hover:text-white" />
        </div>
      </Carousel>
    </Section>
  );
};

const ValueProposition = ({ valueProposition }) => {
  const title = coloredText(valueProposition?.title);
  return (
    <Section
      wrapperClassName="bg-gradient-to-b from-background-blackish via-background-blackish to-background-purplish !pb-24 lg:!pb-40"
      className="grid grid-cols-1 lg:grid-cols-2 gap-8"
    >
      <div className="flex flex-col gap-6 justify-center max-md:items-center max-md:text-center">
        <Pill data-tina-field={tinaField(valueProposition, "sectionTitle")}>
          {valueProposition?.sectionTitle}
        </Pill>
        <h2
          className="h1"
          data-tina-field={tinaField(valueProposition, "title")}
          dangerouslySetInnerHTML={{ __html: title }}
        />
      </div>
      {valueProposition?.valuePropositions?.map((item, index) => {
        const Icon = getIcon(item?.icon);

        return (
          <div
            key={index}
            className="card bg-background-blackish flex flex-col gap-4"
          >
            {Icon && (
              <div className="flex items-center gap-3 mb-12">
                <Icon className="text-primary-300 size-9" />
              </div>
            )}
            <h3 className="h4 font-bold">{item.title}</h3>
            <TextTooltip
              text={item.description}
              className="p-lg text-foreground-300"
              data-tina-field={tinaField(
                valueProposition,
                `valuePropositions.${index}.description`
              )}
            />
          </div>
        );
      })}
    </Section>
  );
};

const WhoWeServe = ({ whoWeServe }) => {
  return (
    <Section
      wrapperClassName="bg-background-blackish"
      className="flex flex-col gap-14"
    >
      <div className="flex flex-col items-center gap-4">
        <Pill data-tina-field={tinaField(whoWeServe, "sectionTitle")}>
          {whoWeServe?.sectionTitle}
        </Pill>
        <h2 className="h1" data-tina-field={tinaField(whoWeServe, "title")}>
          {whoWeServe?.title}
        </h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-6">
        {whoWeServe?.items?.map((item, index) => (
          <div key={index} className="flex gap-3">
            <CircleCheck className="text-black size-8 fill-primary-300 flex-shrink-0" />
            <div className="flex flex-col gap-2">
              <div
                className="h4"
                data-tina-field={tinaField(whoWeServe, `items.${index}.title`)}
              >
                {item.title}
              </div>
              <TextTooltip
                text={item.description}
                data-tina-field={tinaField(
                  whoWeServe,
                  `items.${index}.description`
                )}
                className="p-lg text-foreground-300"
              />
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const ResponsiveCarousel = ({ children, isCarousel }) => {
  const shouldShowCarousel = isCarousel;

  if (!shouldShowCarousel) {
    return <div className="flex gap-6">{children}</div>;
  }

  return (
    <Carousel className="[&>div]:!overflow-visible cursor-grab focus:cursor-grabbing active:cursor-grabbing">
      <CarouselContent className="-ml-6 [&>div]:!pl-6">
        {children}
      </CarouselContent>
    </Carousel>
  );
};

const ResponsiveCarouselItem = ({ children, isCarousel, ...props }) => {
  const shouldShowCarousel = isCarousel;
  if (!shouldShowCarousel) {
    return <div {...props}>{children}</div>;
  }
  return <CarouselItem {...props}>{children}</CarouselItem>;
};

const SocialProof = ({ socialProof }) => {
  const title = coloredText(socialProof?.title);
  const isCarousel = socialProof?.testimonials?.length > 2;

  return (
    <Section
      wrapperClassName="bg-gradient-to-b from-background-blackish to-background-purplish !pt-16 !pb-24 lg:!pb-32"
      className="flex flex-col gap-16 overflow-hidden"
    >
      <div
        className={cn(
          "grid gap-12 lg:gap-8 items-center",
          socialProof?.logos?.length > 0 &&
            "grid-cols-1 lg:grid-cols-[2fr_1.5fr]"
        )}
      >
        <h2
          className={cn(
            "h2 text-balance",
            socialProof?.logos?.length > 0
              ? "max-md:text-center max-md:max-w-[800px] max-md:mx-auto"
              : "text-center max-w-[800px] mx-auto"
          )}
          data-tina-field={tinaField(socialProof, "title")}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <div className="flex gap-8 items-center justify-end">
          {socialProof?.logos?.map((logo, index) => (
            <Link
              href={logo?.link ?? "/"}
              key={index}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                key={index}
                src={logo?.image ?? "/images/placeholder.png"}
                alt={logo?.name ?? "Logo"}
                width={100}
                height={100}
                quality={100}
                className="w-auto h-12 object-contain"
                data-tina-field={tinaField(socialProof, `logos.${index}.image`)}
              />
            </Link>
          ))}
        </div>
      </div>
      <ResponsiveCarousel isCarousel={isCarousel} className="overflow-hidden">
        {socialProof?.testimonials?.map((testimonial, index) => (
          <ResponsiveCarouselItem
            key={index}
            className="basis-5/6 lg:basis-1/3"
            isCarousel={isCarousel}
          >
            <div className="card bg-background-blackish flex flex-col gap-6 h-full">
              <div className="flex items-center gap-4">
                <Image
                  src={testimonial?.image ?? "/images/placeholder.png"}
                  alt={testimonial?.name}
                  width={48}
                  height={48}
                  quality={100}
                  className="rounded-full aspect-square object-cover"
                  data-tina-field={tinaField(
                    socialProof,
                    `testimonials.${index}.image`
                  )}
                />
                <div className="flex flex-col gap-0.5">
                  <h3
                    className="h5"
                    data-tina-field={tinaField(
                      socialProof,
                      `testimonials.${index}.name`
                    )}
                  >
                    {testimonial?.name}
                  </h3>
                  <TextTooltip
                    text={testimonial?.description}
                    className="text-base text-foreground-500"
                    data-tina-field={tinaField(
                      socialProof,
                      `testimonials.${index}.description`
                    )}
                  />
                </div>
              </div>
              <TextTooltip
                className="p-lg text-foreground-300"
                text={testimonial?.quote}
                data-tina-field={tinaField(
                  socialProof,
                  `testimonials.${index}.quote`
                )}
              />
            </div>
          </ResponsiveCarouselItem>
        ))}
      </ResponsiveCarousel>
    </Section>
  );
};
